import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as SEARCH_ICON } from "../../../assets/images/navbar-search-icon.svg";
import { siteApi } from "../../../lib/api";
import config from "../../../lib/config";
import { launchGameById, launchProviderLobby } from "../../../lib/gameLaunch";
import { getIntegratedSportsbookRoute } from "../../../utils/gameCatalog";
import Button from "../../Common/Buttons/Button";
import CloseButtonModal from "../CloseButtonModal";
import Modal from "../Modal";
import { StyledSearchContent } from "./styles";

const SEARCH_MIN_LENGTH = 2;
const SLOT_GAME_LIMIT = 18;
const INITIAL_VENDOR_LIMIT = 20;

const FILTERS = [
  { key: "all", label: "전체" },
  { key: "sports-vendor", label: "스포츠" },
  { key: "casino-vendor", label: "카지노" },
  { key: "slot-vendor", label: "슬롯 게임사" },
  { key: "slot-game", label: "슬롯 게임" },
];

const VENDOR_SEARCH_CONFIG = [
  { key: "sportsVendors", type: "sports-vendor", gameType: "live-sports" },
  { key: "casinoVendors", type: "casino-vendor", gameType: "live-casino" },
  { key: "slotVendors", type: "slot-vendor", gameType: "slots" },
];

const SEARCH_ALIAS_GROUPS = [
  ["pragmatic", "pragmaticplay", "프라그마틱", "프라그마틱플레이"],
  ["evolution", "에볼루션", "에볼루션게이밍"],
  ["sexy", "sexygaming", "섹시", "섹시게이밍"],
  ["microgaming", "마이크로게임", "마이크로게이밍"],
  ["allbet", "올벳", "올벳카지노"],
  ["oriental", "orientallive", "오리엔탈", "오리엔탈라이브"],
  ["dream", "dreamgaming", "드림", "드림게이밍"],
  ["playtech", "플레이텍"],
  ["sagaming", "sa", "SA게이밍", "에스에이", "에스에이게이밍"],
  ["bti", "비티아이", "비티아이스포츠"],
  ["bt1", "비티원", "비티원스포츠"],
  ["saba", "사바", "사바스포츠"],
  ["cq9", "씨큐나인"],
  ["playngo", "playngo", "play'n go", "플레이앤고"],
  ["habanero", "하바네로"],
  ["netent", "넷엔트"],
  ["redtiger", "레드타이거"],
  ["nolimitcity", "노리밋시티"],
  ["booongo", "부운고"],
  ["pgsoft", "피지소프트"],
];

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeText = (value = "") =>
  String(value)
    .replace(/\bBTI\b/gi, "비티아이")
    .replace(/\bBT1\b/gi, "비티원")
    .replace(/\bSABA\b/gi, "사바")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "에스에이")
    .replace(/\s+/g, " ")
    .trim();

const normalizeSearch = (value = "") =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^0-9a-zㄱ-ㅎ가-힣]+/gi, "");

const getSearchAliases = (values) => {
  const normalizedValues = values.filter(Boolean).map(normalizeSearch);
  const aliasValues = new Set(normalizedValues);

  SEARCH_ALIAS_GROUPS.forEach((group) => {
    const normalizedGroup = group.map(normalizeSearch);
    const hasMatch = normalizedGroup.some((alias) =>
      normalizedValues.some((value) => value.includes(alias) || alias.includes(value))
    );

    if (hasMatch) {
      normalizedGroup.forEach((alias) => aliasValues.add(alias));
    }
  });

  return Array.from(aliasValues);
};

const getVendorTitle = (vendor) =>
  normalizeText(
    vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendor?.vendor_name ||
      "게임사"
  );

const getGameTitle = (game) =>
  normalizeText(
    game?.game_name_ko ||
      game?.game_name_kr ||
      game?.name_ko ||
      game?.game_name ||
      game?.name ||
      "슬롯 게임"
  );

const getVendorImage = (vendor) =>
  resolveAssetUrl(
    vendor?.image ||
      vendor?.pc_image ||
      vendor?.mobile_image ||
      vendor?.thumbnail ||
      vendor?.vendor_list?.thumbnails
  );

const getGameImage = (game) =>
  resolveAssetUrl(game?.game_image || game?.image || game?.thumbnail || game?.thumbnail_url);

const matchesVendor = (vendor, query) => {
  if (!query) return true;
  const searchable = getSearchAliases([
    getVendorTitle(vendor),
    vendor?.vendor_name,
    vendor?.vendor_name_kr,
    vendor?.vendor_name_ko,
    vendor?.display_name,
  ]);

  return searchable.some((value) => value.includes(query));
};

const matchesGame = (game, query) => {
  if (!query) return true;
  const searchable = getSearchAliases([
    getGameTitle(game),
    game?.game_name,
    game?.game_name_ko,
    game?.game_name_kr,
    game?.vendor,
    game?.vendor_name,
  ]);

  return searchable.some((value) => value.includes(query));
};

const getVendorSubtitle = (type) => {
  if (type === "sports-vendor") return "스포츠";
  if (type === "casino-vendor") return "카지노 게임사";
  return "슬롯 게임사";
};

const buildVendorResult = (vendor, type) => ({
  id: `${type}:${vendor?.id || vendor?.vendor_name}`,
  type,
  title: getVendorTitle(vendor),
  subtitle: getVendorSubtitle(type),
  image: getVendorImage(vendor),
  vendorName: vendor?.vendor_name,
  raw: vendor,
});

const buildGameResult = (game) => ({
  id: `slot-game:${game?.id || game?.game_id || game?.game_name}`,
  type: "slot-game",
  title: getGameTitle(game),
  subtitle: normalizeText(game?.vendor || game?.vendor_name || "슬롯 게임"),
  image: getGameImage(game),
  gameId: game?.game_id || game?.id,
  raw: game,
});

const SearchModal = ({ triggerClassName = "", triggerAriaLabel = "검색" }) => {
  const navigate = useNavigate();
  const requestRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sportsVendors, setSportsVendors] = useState([]);
  const [casinoVendors, setCasinoVendors] = useState([]);
  const [slotVendors, setSlotVendors] = useState([]);
  const [slotGames, setSlotGames] = useState([]);
  const [isVendorLoading, setIsVendorLoading] = useState(false);
  const [isGameLoading, setIsGameLoading] = useState(false);
  const [error, setError] = useState("");
  const normalizedQuery = useMemo(() => normalizeSearch(debouncedQuery), [debouncedQuery]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 260);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, query]);

  useEffect(() => {
    if (!isOpen || sportsVendors.length || casinoVendors.length || slotVendors.length) {
      return undefined;
    }

    let isMounted = true;
    setIsVendorLoading(true);
    setError("");

    Promise.all(
      VENDOR_SEARCH_CONFIG.map((configItem) =>
        siteApi.getGameVendors({
          game_type: configItem.gameType,
          order_by_name: "sequence",
          order_by_type: "ASC",
          limit: "ALL",
        })
      )
    )
      .then((responses) => {
        if (!isMounted) return;
        responses.forEach((response, index) => {
          const rows = response.success ? getRows(response) : [];
          const targetKey = VENDOR_SEARCH_CONFIG[index].key;

          if (targetKey === "sportsVendors") setSportsVendors(rows);
          if (targetKey === "casinoVendors") setCasinoVendors(rows);
          if (targetKey === "slotVendors") setSlotVendors(rows);
        });

        if (responses.some((response) => !response.success)) {
          setError("일부 게임사 목록을 불러오지 못했습니다.");
        }
      })
      .catch(() => {
        if (isMounted) setError("게임사 목록을 불러오지 못했습니다.");
      })
      .finally(() => {
        if (isMounted) setIsVendorLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [casinoVendors.length, isOpen, slotVendors.length, sportsVendors.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const canSearchSlotGames = activeFilter === "all" || activeFilter === "slot-game";

    if (!canSearchSlotGames || normalizedQuery.length < SEARCH_MIN_LENGTH) {
      setSlotGames([]);
      setIsGameLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    setIsGameLoading(true);

    siteApi
      .getGames({
        page: 1,
        limit: SLOT_GAME_LIMIT,
        game_type: "slots",
        search: debouncedQuery,
        order_by_name: "sequence",
        order_by_type: "ASC",
      })
      .then((response) => {
        if (requestId !== requestRef.current) return;
        setSlotGames(response.success ? getRows(response) : []);
      })
      .catch(() => {
        if (requestId === requestRef.current) setSlotGames([]);
      })
      .finally(() => {
        if (requestId === requestRef.current) setIsGameLoading(false);
      });

    return undefined;
  }, [activeFilter, debouncedQuery, isOpen, normalizedQuery.length]);

  const vendorResults = useMemo(() => {
    const sportsResults = sportsVendors
      .filter((vendor) => matchesVendor(vendor, normalizedQuery))
      .map((vendor) => buildVendorResult(vendor, "sports-vendor"));
    const casinoResults = casinoVendors
      .filter((vendor) => matchesVendor(vendor, normalizedQuery))
      .map((vendor) => buildVendorResult(vendor, "casino-vendor"));
    const slotResults = slotVendors
      .filter((vendor) => matchesVendor(vendor, normalizedQuery))
      .map((vendor) => buildVendorResult(vendor, "slot-vendor"));

    return [...sportsResults, ...casinoResults, ...slotResults];
  }, [casinoVendors, normalizedQuery, slotVendors, sportsVendors]);

  const gameResults = useMemo(
    () =>
      slotGames
        .filter((game) => matchesGame(game, normalizedQuery))
        .map(buildGameResult),
    [normalizedQuery, slotGames]
  );

  const visibleResults = useMemo(() => {
    const rows = [
      ...(activeFilter === "all" || activeFilter === "sports-vendor"
        ? vendorResults.filter((result) => result.type === "sports-vendor")
        : []),
      ...(activeFilter === "all" || activeFilter === "casino-vendor"
        ? vendorResults.filter((result) => result.type === "casino-vendor")
        : []),
      ...(activeFilter === "all" || activeFilter === "slot-vendor"
        ? vendorResults.filter((result) => result.type === "slot-vendor")
        : []),
      ...(activeFilter === "all" || activeFilter === "slot-game" ? gameResults : []),
    ];

    if (normalizedQuery) return rows;
    return rows.slice(0, INITIAL_VENDOR_LIMIT);
  }, [activeFilter, gameResults, normalizedQuery, vendorResults]);

  const resultCount = visibleResults.length;
  const isLoading = isVendorLoading || isGameLoading;

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleResultClick = async (result) => {
    if (result.type === "casino-vendor") {
      await launchProviderLobby(result.raw, {
        label: result.title,
        gameType: "live-casino",
        meta: "카지노게임",
      });
      return;
    }

    if (result.type === "sports-vendor") {
      const integratedRoute = getIntegratedSportsbookRoute(result.raw);
      if (integratedRoute) {
        navigate(integratedRoute);
        handleClose();
        return;
      }

      const lobbyId = result.raw?.vendor_list?.lobby_id || result.raw?.lobby_id;
      if (lobbyId) {
        await launchProviderLobby(result.raw, {
          label: result.title,
          gameType: "live-sports",
          meta: "스포츠",
        });
      } else {
        navigate("/sports?type=DOMESTIC");
        handleClose();
      }
      return;
    }

    if (result.type === "slot-vendor") {
      const vendorName = encodeURIComponent(result.vendorName || "");
      navigate(`/slots${vendorName ? `?vendor=${vendorName}` : ""}`);
      handleClose();
      return;
    }

    if (result.type === "slot-game") {
      await launchGameById(result.gameId, {
        label: result.title,
        recentPlay: {
          kind: "slot-game",
          gameType: "slots",
          gameId: result.gameId,
          vendorName: result.raw?.vendor || result.raw?.vendor_name || "",
          title: result.title,
          image: result.image,
          meta: result.subtitle || "슬롯게임",
        },
      });
    }
  };

  return (
    <>
      <Button
        className={triggerClassName}
        style={triggerClassName ? undefined : { marginLeft: "1rem", marginRight: "1rem" }}
        onClick={() => setIsOpen(true)}
        aria-label={triggerAriaLabel}
      >
        <SEARCH_ICON />
      </Button>

      <Modal
        maxWidth={1180}
        maxHeight="88vh"
        isOpen={isOpen}
        onClose={handleClose}
        panelClassName="search-dialog-panel"
      >
        <StyledSearchContent>
          <CloseButtonModal onClick={handleClose} />

          <div className="search-modal-header">
            <span>SEARCH</span>
            <h2>게임 검색</h2>
            <p>스포츠, 카지노, 슬롯 게임을 한 번에 찾아보세요.</p>
          </div>

          <label className="global-game-search-box">
            <SEARCH_ICON aria-hidden="true" />
            <input
              type="search"
              value={query}
              autoFocus
              placeholder="스포츠, 게임사, 슬롯 게임 검색"
              onChange={(event) => setQuery(event.target.value)}
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")} aria-label="검색어 지우기">
                지우기
              </button>
            ) : null}
          </label>

          <div className="search-filter-tabs" role="tablist" aria-label="검색 범위">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={filter.key === activeFilter ? "is-active" : ""}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="search-meta-row">
            <span>
              {isLoading
                ? "검색 중"
                : normalizedQuery
                  ? `${resultCount.toLocaleString("ko-KR")}개 결과`
                  : "추천 게임사"}
            </span>
            {normalizedQuery.length > 0 && normalizedQuery.length < SEARCH_MIN_LENGTH ? (
              <em>슬롯 게임명 검색은 2글자 이상부터 시작됩니다.</em>
            ) : null}
          </div>

          {error ? <div className="search-state is-error">{error}</div> : null}

          <div className="search-result-grid" aria-busy={isLoading}>
            {isLoading && !visibleResults.length
              ? Array.from({ length: 8 }).map((_, index) => (
                  <span className="search-result-skeleton" key={`search-skeleton-${index}`} />
                ))
              : visibleResults.map((result) => (
                  <button
                    type="button"
                    className={`search-result-card ${result.type}`}
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                  >
                    <span className={`result-card ${result.image ? "has-image" : "no-image"}`}>
                      {result.image ? <img src={result.image} alt="" loading="lazy" /> : null}
                      <span className="result-badge">{result.subtitle}</span>
                      <span className="result-fallback">{result.title.slice(0, 2)}</span>
                      <span className="result-title">
                        <strong>{result.title}</strong>
                      </span>
                      <span className="result-hover-layer">
                        <span className="search-card-play" aria-hidden="true" />
                        <strong>{result.title}</strong>
                        <em>{result.subtitle}</em>
                      </span>
                    </span>
                  </button>
                ))}
          </div>

          {!isLoading && !error && visibleResults.length === 0 ? (
            <div className="search-state">
              {normalizedQuery
                ? "검색 결과가 없습니다."
                : "검색어를 입력하거나 검색 범위를 선택해주세요."}
            </div>
          ) : null}
        </StyledSearchContent>
      </Modal>
    </>
  );
};

export default SearchModal;
